import React, {createContext, useState, useEffect, useCallback} from "react"
import {useQueryClient} from "react-query"
import {CognitoUser, AuthenticationDetails, CookieStorage} from "amazon-cognito-identity-js"
import userPool from "./userpool.js"
import {setLocalItem} from "utils/utils"

const AuthContext = createContext()

const Auth = (props) => {

    const endpoint = "https://xvrj76eo17.execute-api.eu-west-1.amazonaws.com/dev"
    const queryClient = useQueryClient()
    const [admin, setAdmin] = useState(false)
    const [superuser, setSuperuser] = useState(false)
    const [idToken, setIdToken] = useState()
    const [user, setUser] = useState()
    const [loading, setLoading] = useState(true)
    const [username, setUsername] = useState('')
    const [ready, setReady] = useState(false)
    const [loggedIn, setLoggedIn] = useState(() => {
        const saved = localStorage.getItem("loggedIn")
        const initialValue = JSON.parse(saved)
        return initialValue || false
    })

    const API = window.apigClientFactory.newClient()

    const reset = useCallback(() => {
        setAdmin(false)
        setSuperuser(false)
        setIdToken(null)
        setUser(null)
        setUsername('')
        setLoggedIn(false)
        queryClient.invalidateQueries()
    }, [queryClient])


    const set = (session) => {
        setLoggedIn(true)
        const payload = session.getIdToken().decodePayload()
        setUser(payload)
        setUsername(payload['cognito:username'])
        const groups = payload['cognito:groups'] || []
        setAdmin(groups.includes('Admin'))
        setSuperuser(groups.includes('Superuser'))
        setReady(true)
    }


    const getSession = async () => {
        setLoading(true)
        return await new Promise((resolve, reject) => {
            const user = userPool.getCurrentUser()
            if (user) {
                setUsername(user.username)
                user.getSession((err, session) => {
                    if (err) {
                        reset()
                        reject()
                    }
                    else {
                        set(session)
                        resolve(session)
                    }
                    setLoading(false)
                })
            }
            else {
                reset()
                reject()
            }   
        })
    }
    
    const getToken = async () => {
        return await new Promise((resolve, reject) => {
            const user = userPool.getCurrentUser()
            if (user) {
                user.getSession((err, session) => {
                    if (err) {
                        reject()
                    }
                    else {
                        const token = session.getIdToken().getJwtToken()
                        setIdToken(token)
                        resolve(token)
                    }
                })
            }
            else {
                reject()
            }
        })
    }
    
    
    
    const requestVerificationCode = async (username) => {
        return await new Promise((resolve, reject) => {
            const user = new CognitoUser({
                Username: username,
                Pool: userPool
            })
            
            user.forgotPassword({
                onSuccess: function (result) {
                    resolve(result)
                },
                onFailure: function (err) {
                    reject(err)
                }
            })
        })        
    }
    
    const changePassword = async (username, verificationCode, newPassword) => {
        return await new Promise((resolve, reject) => {
            const user = new CognitoUser({
                Username: username,
                Pool: userPool
            })
            
            user.confirmPassword(verificationCode, newPassword, {
      			onSuccess(result) {
      				resolve(result)
      			},
      			onFailure(err) {
          			reject(err)
      			}
      		})
      	})	
    }
    
    
    
    const call = (api, params, body, callback) => {
        if (body == null) body = { app: "gss" }
        getToken().then((token) => {
            const headers = { 'Authorization': token }
            const par = { headers: headers }
            api(params, body, par).then(callback).catch(callback)
        }).catch(() => {})
    }
   	
   	const auth = async (api, params={}, body=null) => {        
        return await new Promise((resolve, reject) => {
            if (body == null) body = { app: "gss" }
            getToken().then((token) => {
                const headers = { 'Authorization': token }
                const par = { headers: headers }
                api(params, body, par)
                    .then(response => resolve(response))
                    .catch(response => reject(response))
            }).catch(err => reject(err))
        })
    }
    
    const login = async (username, password) => {
        return await new Promise((resolve, reject) => { 
            const userData = {
                Username: username,
                Pool: userPool
            }
            if (process.env.NODE_ENV === 'production') {
                userData.Storage = new CookieStorage({secure: false, domain: "gss.rs"})  
            }                   
            const user = new CognitoUser(userData)
            
            const authDetails = new AuthenticationDetails({
                Username: username,
                Password: password
            })
            
            user.authenticateUser(authDetails, {
                onSuccess: (data) => {
                    set(data)
                    resolve(data)
                },
                onFailure: (err) => {
                    console.error("onFailure:", err)
                    reset()
                    reject(err)
                },
                newPasswordRequired: (data) => {
                    console.log('newPasswordRequired:', data)
                    set(data)
                    resolve(data)
                }
            })        
        })
    
        
    }
    
    const logout = async () => {
        const promise = new Promise((resolve, _reject) => {
            const user = userPool.getCurrentUser()
            if (user) user.signOut()
            resolve()
        })
        reset()
        await promise
    }

    useEffect(() => {
        getSession().then(() => {}).catch(() => {})
    }, [])
    
    useEffect(() => {
        setLocalItem("loggedIn", loggedIn)
    }, [loggedIn])

    return <AuthContext.Provider value={{login, getSession, loading, logout, admin, superuser, getToken, call, auth, API, requestVerificationCode, changePassword, loggedIn, username, user, endpoint, ready}}>
        {props.children}
    </AuthContext.Provider>
}

export default AuthContext
export { Auth }
